import React, { useState, useEffect } from 'react';
import Layout from '../components/Layout';
import CreateOrEditTalentModal from '../components/CreateOrEditTalentModal';
import TalentTable from '../components/TalentTable';
import TalentMap from '../components/TalentMap';
import SalesResidenceInfoModal from '../components/SalesResidenceInfoModal';
import FilterPanel from '../components/FilterPanel';
import SalesAnalytics from '../components/SalesAnalytics';
import SystemNotificationPanel from '../components/SystemNotificationPanel';
import ClientManagementPanel from '../components/ClientManagementPanel';
import SpecifiedSkillNewsPanel, { NewsActionCard } from '../components/SpecifiedSkillNewsPanel';
import { useAuth } from '../context/AuthContext';
import { useTalentSearch } from '../hooks/useTalentSearch';
import { useTranslation } from 'react-i18next';
import { getInitialFilters, getFilterConfigByRole } from '../config/filterConfig';

export default function SalesDashboard() {
    const { user } = useAuth();
    const [view, setView] = useState('home');
    const { t } = useTranslation();

    const {
        talents,
        isLoading,
        searchTerm,
        setSearchTerm,
        sortBy,
        setSortBy,
        sortDirection,
        setSortDirection,
        fetchTalents,
        currentPage,
        setCurrentPage,
        totalPages,
        totalCount,
        feeTotals,
        pageSize,
        filters,
        setFilters,
    } = useTalentSearch();

    // 初始化筛选状态
    useEffect(() => {
        const config = getFilterConfigByRole(user.role);
        const initialFilters = getInitialFilters(config);
        setFilters(initialFilters);
    }, [user.role, setFilters]);

    const [isCreateOrEditModalOpen, setIsCreateOrEditModalOpen] = useState(false);
    const [editingTalent, setEditingTalent] = useState(null);

    const [isViewModalOpen, setIsViewModalOpen] = useState(false);
    const [viewingTalent, setViewingTalent] = useState(null);

    const handleUpserted = () => {
        if (view === 'list') {
            fetchTalents();
        }
    };

    const handleEdit = (talent) => {
        setEditingTalent(talent);
        setIsCreateOrEditModalOpen(true);
    };

    const handleAddNew = () => {
        setEditingTalent(null);
        setIsCreateOrEditModalOpen(true);
    };

    const handleCloseModal = () => {
        setIsCreateOrEditModalOpen(false);
        setEditingTalent(null);
    };

    const handleViewMyTalents = () => {
        setView('list');
        fetchTalents();
    };

    const handleViewDetails = (talent) => {
        setViewingTalent(talent);
        setIsViewModalOpen(true);
    };

    const handleViewEdit = (talent) => {
        // For Sales users, open the combined view/edit modal
        setViewingTalent(talent);
        setIsViewModalOpen(true);
    };

    // 只在非首页时显示顶部操作按钮
    const actions = view !== 'home' ? (
        <div className="grid grid-cols-2 sm:flex sm:flex-wrap gap-2 w-full sm:w-auto">
            <button onClick={() => setView('home')} className="px-3 py-2 text-xs sm:text-sm font-semibold rounded-lg shadow-md bg-purple-600 text-white hover:bg-purple-700 min-h-[44px] flex items-center justify-center">
                <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6"></path>
                </svg>
                {t('backToHome')}
            </button>
            {view !== 'clients' && (
                <>
                    <button onClick={handleAddNew} className="px-3 py-2 text-xs sm:text-sm font-semibold rounded-lg shadow-md bg-green-600 text-white hover:bg-green-700 min-h-[44px] flex items-center justify-center">{t('newTalent')}</button>
                    <button onClick={handleViewMyTalents} className="px-3 py-2 text-xs sm:text-sm font-semibold rounded-lg shadow-md bg-white text-gray-700 border hover:bg-gray-100 min-h-[44px] flex items-center justify-center">{t('myTalents')}</button>
                    <button onClick={() => setView('map')} className="px-3 py-2 text-xs sm:text-sm font-semibold rounded-lg shadow-md bg-blue-600 text-white hover:bg-blue-700 min-h-[44px] flex items-center justify-center col-span-2 sm:col-span-1">{t('talentMap')}</button>
                </>
            )}
        </div>
    ) : null;

    return (
        <>
            <CreateOrEditTalentModal
                isOpen={isCreateOrEditModalOpen}
                onClose={handleCloseModal}
                onFinished={handleUpserted}
                existingTalent={editingTalent}
            />
            <SalesResidenceInfoModal
                isOpen={isViewModalOpen}
                onClose={() => setIsViewModalOpen(false)}
                talent={viewingTalent}
            />
            <Layout title={t('salesDashboard')} actions={actions} fullWidth={view === 'list' || view === 'clients' || view === 'news'}>
                {view === 'home' && (
                    <div className="space-y-8">
                        {/* 区域标题：快速操作 */}
                        <div className="section-header">
                            <div className="section-header-icon">
                                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 10V3L4 14h7v7l9-11h-7z" />
                                </svg>
                            </div>
                            <div>
                                <h2 className="section-header-title">クイックアクション</h2>
                                <p className="section-header-subtitle">よく使う機能にすばやくアクセス</p>
                            </div>
                        </div>

                        {/* Bento Grid 快速操作卡片 */}
                        <div className="dashboard-bento-grid">
                            {/* 新規人材登録 */}
                            <button
                                onClick={handleAddNew}
                                className="action-card action-card--green"
                            >
                                <div className="action-card-icon">
                                    <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M18 9v3m0 0v3m0-3h3m-3 0h-3m-2-5a4 4 0 11-8 0 4 4 0 018 0zM3 20a6 6 0 0112 0v1H3v-1z" />
                                    </svg>
                                </div>
                                <span className="action-card-title">{t('newTalent')}</span>
                                <span className="action-card-subtitle">新しい人材を登録</span>
                            </button>

                            {/* 自分の人材 */}
                            <button
                                onClick={handleViewMyTalents}
                                className="action-card action-card--blue"
                            >
                                <div className="action-card-icon">
                                    <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" />
                                    </svg>
                                </div>
                                <span className="action-card-title">{t('myTalents')}</span>
                                <span className="action-card-subtitle">担当人材リスト</span>
                            </button>

                            {/* 人材マップ */}
                            <button
                                onClick={() => setView('map')}
                                className="action-card action-card--purple"
                            >
                                <div className="action-card-icon">
                                    <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7" />
                                    </svg>
                                </div>
                                <span className="action-card-title">{t('talentMap')}</span>
                                <span className="action-card-subtitle">地図で確認</span>
                            </button>

                            {/* 取引先管理 */}
                            <button
                                onClick={() => setView('clients')}
                                className="action-card action-card--indigo"
                            >
                                <div className="action-card-icon">
                                    <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6M8 4h8a2 2 0 012 2v14l-4-2-4 2-4-2V6a2 2 0 012-2z" />
                                    </svg>
                                </div>
                                <span className="action-card-title">{t('clientManagement.quickAction')}</span>
                                <span className="action-card-subtitle">担当取引先の契約書</span>
                            </button>

                            <NewsActionCard onClick={() => setView('news')} />
                        </div>

                        {/* 系统通知 - 移到快速操作卡片之后 */}
                        <SystemNotificationPanel />

                        {/* 统计分析 */}
                        <SalesAnalytics />

                        {/* 支持信息区域 */}
                        <div className="welcome-section text-center">
                            <h2 className="text-xl font-semibold text-gray-800 mb-2">{t('welcome')}, {user.username}!</h2>
                            <p className="text-base text-[var(--text-secondary)] mb-4">{t('startWork')}</p>
                            <div className="pt-4 border-t border-[var(--border)]">
                                <p className="text-sm text-[var(--text-muted)] mb-4">{t('feedbackPrompt')}</p>
                                <a
                                    href="https://www.larksuite.com/invitation/page/add_contact/?token=93fn2903-69a2-4e4b-a8be-2d8b49qvicbv"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="support-link"
                                >
                                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                                    </svg>
                                    {t('contactSupport')}
                                </a>
                            </div>
                        </div>
                    </div>
                )}
                {view === 'list' && (
                    <div className="flex h-screen">
                        {/* 筛选面板 */}
                        <FilterPanel
                            role={user.role}
                            filters={filters}
                            onFiltersChange={setFilters}
                        />

                        {/* 主内容区域 */}
                        <div className="flex-1 overflow-auto">
                            <div className="mb-4 bg-[var(--surface)] p-4 rounded-lg shadow-[var(--shadow-sm)] border border-[var(--border)]">
                                <div className="text-sm text-[var(--text-secondary)] mb-2">
                                    {t('totalRecords')}: {totalCount}件 | {t('page')} {currentPage} / {totalPages}
                                </div>
                                <input
                                    type="text"
                                    placeholder={t('searchNameNumber')}
                                    className="w-full px-4 py-2 border border-[var(--border)] rounded-lg focus:ring-2 focus:ring-[var(--primary-200)] focus:border-[var(--primary-500)] transition-all duration-200"
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                />
                            </div>
                            <TalentTable
                                talents={talents}
                                isLoading={isLoading}
                                feeTotals={feeTotals}
                                userInfo={user}
                                onDataRefresh={fetchTalents}
                                onEdit={handleEdit}
                                onViewDetails={handleViewDetails}
                                onViewEdit={handleViewEdit}
                                sortBy={sortBy}
                                setSortBy={setSortBy}
                                sortDirection={sortDirection}
                                setSortDirection={setSortDirection}
                            />

                            {/* 分页控件 */}
                            {totalPages > 1 && (
                                <div className="mt-4 bg-white p-4 rounded-lg shadow">
                                    <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
                                        {/* 分页信息 */}
                                        <div className="text-sm text-gray-600">
                                            {t('showingResults', {
                                                start: (currentPage - 1) * pageSize + 1,
                                                end: Math.min(currentPage * pageSize, totalCount),
                                                total: totalCount
                                            })}
                                        </div>

                                        {/* 分页按钮 */}
                                        <div className="flex items-center space-x-2">
                                            <button
                                                onClick={() => setCurrentPage(1)}
                                                disabled={currentPage === 1}
                                                className="px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                                            >
                                                {t('firstPage')}
                                            </button>

                                            <button
                                                onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                                                disabled={currentPage === 1}
                                                className="px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                                            >
                                                {t('previousPage')}
                                            </button>

                                            <span className="px-4 py-2 text-sm text-gray-700">
                                                {t('pageInfo', { current: currentPage, total: totalPages })}
                                            </span>

                                            <button
                                                onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                                                disabled={currentPage === totalPages}
                                                className="px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                                            >
                                                {t('nextPage')}
                                            </button>

                                            <button
                                                onClick={() => setCurrentPage(totalPages)}
                                                disabled={currentPage === totalPages}
                                                className="px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                                            >
                                                {t('lastPage')}
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                )}

                {view === 'map' && <TalentMap />}
                {view === 'clients' && <ClientManagementPanel />}
                {view === 'news' && <SpecifiedSkillNewsPanel />}
            </Layout>
        </>
    );
}
